"use client";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useState, useRef, useEffect } from "react";
import { reviews, Review } from "./data";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./styles.css";
import { ReviewDialog } from "./review-dialog";

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-[6px]">
      {[...Array(5)].map((_, i) => (
        <svg
          width="19"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3 0C1.34315 0 0 1.34315 0 3V17C0 18.6569 1.34315 20 3 20H17C18.6569 20 20 18.6569 20 17V3C20 1.34315 18.6569 0 17 0H3ZM13.046 11.015L15.4606 8.80857C15.6778 8.61015 15.5689 8.24855 15.2782 8.20315L13.226 7.88262L12.0419 7.69461C11.9291 7.6767 11.832 7.60495 11.7819 7.50233L11.2698 6.45534L10.3742 4.61896C10.2486 4.36135 9.88376 4.35505 9.74931 4.60816L8.24105 7.44783C8.18751 7.54865 8.08826 7.6171 7.975 7.63132L6.78507 7.78068L4.72403 8.03532C4.43221 8.07137 4.31165 8.42895 4.52207 8.63433L6.8164 10.873C6.90088 10.9553 6.93827 11.0747 6.91589 11.1905L6.69737 12.3218L6.31064 14.3016C6.25511 14.5859 6.54865 14.8112 6.80887 14.6839L9.69151 13.2746C9.78672 13.228 9.89787 13.2271 9.99389 13.2719L13.056 14.7039C13.3173 14.8262 13.6062 14.5984 13.5482 14.3158L12.939 11.3441C12.9143 11.2233 12.9549 11.0983 13.046 11.015Z"
            fill="#0FC585"
          ></path>
        </svg>
      ))}
    </div>
  );
}

function ReviewCard({ review }: { review: Review }) {
  return (
    <Card className="review-card border-none shadow-sm !rounded-2xl">
      <div className="space-y-4 p-6">
        <div className="flex items-center gap-2 justify-between">
          <StarRating rating={review.rating} />
          <div className="text-sm text-gray-500">{review.date}</div>
        </div>
        <h3 className="font-semibold text-lg">{review.title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">
          {review.content}
        </p>
        <div className="flex items-center gap-2 justify-between pt-4">
          <div className="flex items-center gap-2">
            <img
              src={`/${review.author.country}-flag.svg`}
              alt={review.author.country}
              width={20}
              height={15}
              className="rounded"
            />
            <span className="font-medium">{review.author.name}</span>
          </div>
          <div className="verified-badge">
            <span className="text-[#a6a6b7] font-medium text-[13.5px]">Verified Purchase</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14 7.00082C14 6.3602 13.4832 5.84168 12.8427 5.83852C13.136 5.71484 13.3532 5.48492 13.4657 5.21216C13.5799 4.93784 13.5894 4.61912 13.4657 4.32259C13.2216 3.73115 12.5447 3.44888 11.9518 3.69148C12.1753 3.46473 12.2878 3.1698 12.2878 2.87326C12.2878 2.57516 12.1737 2.27863 11.947 2.05189C11.4952 1.59837 10.7612 1.59837 10.3078 2.04872C10.4283 1.75378 10.4187 1.43666 10.3062 1.1639C10.192 0.889576 9.97329 0.658076 9.67682 0.534382C9.08551 0.290187 8.40857 0.569266 8.16126 1.15755C8.15968 0.838832 8.02968 0.550242 7.82042 0.340926C7.60957 0.130032 7.31946 0 6.99923 0C6.35875 0 5.84035 0.516927 5.83719 1.15755C5.71354 0.864203 5.48367 0.646971 5.21098 0.532787C4.93672 0.418619 4.61807 0.409105 4.32161 0.532787C3.7303 0.778568 3.44809 1.45405 3.69064 2.04712C3.46394 1.82196 3.16908 1.71095 2.87261 1.71095C2.57458 1.71095 2.27811 1.82512 2.05142 2.05188C1.5996 2.5038 1.59801 3.23795 2.04825 3.69147C1.75338 3.57096 1.43789 3.58047 1.16363 3.69305C0.889374 3.80722 0.657926 4.02604 0.53426 4.32257C0.290121 4.91401 0.569137 5.59111 1.15728 5.83847C0.838641 5.84006 0.550117 5.97008 0.340848 6.1794C0.130002 6.39029 0 6.68046 0 7.00077C0 7.64139 0.516809 8.15991 1.15569 8.16307C0.862411 8.28676 0.645229 8.51667 0.532666 8.78943C0.418523 9.06534 0.410598 9.38247 0.532666 9.679C0.778391 10.2704 1.45372 10.5527 2.04665 10.3101C1.82154 10.5369 1.71056 10.8318 1.71056 11.1283C1.71056 11.4264 1.8247 11.723 2.05141 11.9497C2.50323 12.4016 3.23722 12.4032 3.69063 11.9529C3.57015 12.2478 3.57966 12.5649 3.69221 12.8377C3.80636 13.112 4.02512 13.3435 4.32159 13.4672C4.9129 13.713 5.59143 13.4323 5.83873 12.8425C5.84032 13.1612 5.97032 13.4498 6.17958 13.6591C6.39043 13.87 6.68054 14 7.00077 14C7.64125 14 8.15965 13.4831 8.16281 12.8425C8.28646 13.1358 8.51633 13.353 8.78902 13.4672C9.06328 13.5814 9.38193 13.5893 9.67839 13.4672C10.2697 13.223 10.5519 12.5459 10.3094 11.9529C10.5361 12.1765 10.8309 12.289 11.1274 12.289C11.4254 12.289 11.7219 12.1749 11.9486 11.9481C12.402 11.4946 12.402 10.762 11.9517 10.3085C12.2466 10.429 12.5637 10.4195 12.8364 10.3069C13.1106 10.1928 13.3421 9.97396 13.4657 9.67743C13.7115 9.08598 13.4309 8.40889 12.8427 8.16153C13.1614 8.15994 13.4499 8.02992 13.6592 7.8206C13.87 7.61129 14 7.3211 14 7.00082V7.00082ZM10.208 5.89877C10.0891 6.0177 9.9702 6.13662 9.85129 6.25556C9.22827 6.87872 8.60525 7.50189 7.98379 8.12349C7.58111 8.52626 7.17845 8.929 6.77418 9.33337C6.43017 9.67746 5.8864 9.66635 5.53764 9.33971C5.48374 9.28897 5.43142 9.23982 5.37752 9.19066C4.85121 8.69751 4.32489 8.20436 3.79854 7.7128C3.45135 7.38614 3.46404 6.80737 3.7922 6.47598C4.13462 6.1303 4.68157 6.14457 5.02874 6.46964C5.08264 6.5188 5.13496 6.56954 5.18886 6.61869C5.50275 6.91363 5.81823 7.20857 6.13212 7.50192C6.6727 6.95962 7.2149 6.41889 7.75548 5.87818C8.15816 5.47541 8.56081 5.07267 8.96509 4.6683C9.30275 4.33056 9.86237 4.32581 10.2016 4.66196C10.5393 4.99494 10.5456 5.56104 10.208 5.89878L10.208 5.89877Z"
                fill="#70D2E8"
              ></path>
            </svg>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function ReviewSection({
  mainColor,
}: {
  mainColor: string;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === reviews.length - 1 ? 0 : prev + 1));
  };

  const getVisibleReviews = (): Array<Review | { review: Review; position: number }> => {
    if (isMobile) return reviews;

    const visibleIndexes = [-2, -1, 0, 1, 2];
    return visibleIndexes.map((offset) => {
      let index = currentIndex + offset;
      if (index < 0) index = reviews.length + index;
      if (index >= reviews.length) index = index - reviews.length;
      return { review: reviews[index], position: offset };
    });
  };

  return (
    <section className="py-16 px-4 bg-[#fafbff]" id="client-stories">
      <div className="max-w-full mx-auto">
        <div className="mx-auto max-w-[1250px] items-center text-center mb-12">
          <div
            className="text-[16px] md:text-[18px] font-normal mb-3 pt-2 md:text-center text-left"
            style={{ color: mainColor }}
          >
            Still not convinced?
          </div>
          <div className="md:text-[46px] text-[32px] md:leading-normal leading-tight font-semibold pb-8 md:pb-24 md:text-center text-left">
            Here's what our customers say
          </div>

          <div className="flex items-center gap-8 mb-6 flex-wrap justify-between">
            <div className="flex items-center gap-2">
              <div className="relative w-[65px] h-[65px] bg-white rounded-full">
                <div className="absolute inset-[4px] rounded-full border-[4px] flex flex-col items-center justify-center" style={{ borderColor: mainColor }}>
                  <span className="text-2xl font-medium leading-none" style={{ color: mainColor }}>
                    5
                  </span>
                  <span className="text-[10px] text-gray-400">of 5</span>
                </div>
              </div>
              <div className="flex flex-col items-start">
                <div className="flex items-center gap-2">
                  <div className="flex flex-row md:items-center gap-2">
                    <span className="text-[16px] text-[#4f535f] text-left">Customers rate us</span>
                    <span className="font-semibold text-left" style={{ color: mainColor }}>Superb</span>
                  </div>
                  <div className="hidden md:flex gap-1 ml-2">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        width="24"
                        height="24"
                        viewBox="0 0 19 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.80729 0.655781C9.19147 -0.218594 9.8084 -0.218594 10.193 0.655781L12.6447 6.22902L18.1772 7.06861C19.0648 7.20264 19.2653 7.86154 18.6265 8.54377L14.588 12.8227L15.5787 18.9209C15.7303 19.8773 15.216 20.2677 14.4315 19.8067L9.50003 16.8973L4.56876 19.8067C3.78387 20.2677 3.26973 19.8773 3.42116 18.9209L4.41182 12.8227L0.373554 8.54377C-0.265227 7.86154 -0.0649666 7.20264 0.822905 7.06861L6.35495 6.22902L8.80729 0.655781Z"
                          fill="#FF553C"
                        />
                      </svg>
                    ))}
                  </div>
                </div>
                <div className="flex md:hidden gap-1 mt-2 justify-center">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      width="24"
                      height="24"
                      viewBox="0 0 19 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.80729 0.655781C9.19147 -0.218594 9.8084 -0.218594 10.193 0.655781L12.6447 6.22902L18.1772 7.06861C19.0648 7.20264 19.2653 7.86154 18.6265 8.54377L14.588 12.8227L15.5787 18.9209C15.7303 19.8773 15.216 20.2677 14.4315 19.8067L9.50003 16.8973L4.56876 19.8067C3.78387 20.2677 3.26973 19.8773 3.42116 18.9209L4.41182 12.8227L0.373554 8.54377C-0.265227 7.86154 -0.0649666 7.20264 0.822905 7.06861L6.35495 6.22902L8.80729 0.655781Z"
                        fill="#FF553C"
                      />
                    </svg>
                  ))}
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-[#84909f] text-[15px]">
                    <span style={{ color: mainColor }}>5 / 5</span> - based on 68
                    reviews
                  </span>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              className="ml-8 whitespace-nowrap hover:bg-gray-50 text-lg px-8 py-6 md:flex hidden"
              onClick={() => setIsReviewDialogOpen(true)}
            >
              Submit Your Review
            </Button>
          </div>
        </div>

        <div className="relative w-full">
          <div className="review-carousel">
            <div
              ref={containerRef}
              className="review-container"
            >
              {isMobile ? (
                reviews.map((review) => (
                  <div key={review.id} className="h-full">
                    <ReviewCard review={review} />
                  </div>
                ))
              ) : (
                getVisibleReviews().map((item) => {
                  if ('position' in item) {
                    return (
                      <div
                        key={`${item.review.id}-${item.position}`}
                        className={cn(
                          "transition-all duration-300 h-full",
                          Math.abs(item.position) > 1 && "opacity-35 scale-100"
                        )}
                      >
                        <ReviewCard review={item.review} />
                      </div>
                    );
                  }
                  return (
                    <div key={item.id} className="h-full">
                      <ReviewCard review={item} />
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="hidden md:flex justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePrevious}
              className="rounded-full"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleNext}
              className="rounded-full"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex md:hidden justify-center mt-8">
            <Button
              variant="outline"
              className="whitespace-nowrap hover:bg-gray-50 text-lg px-8 py-6"
              onClick={() => setIsReviewDialogOpen(true)}
            >
              Submit Your Review
            </Button>
          </div>
        </div>
      </div>

      <ReviewDialog 
        isOpen={isReviewDialogOpen}
        onClose={() => setIsReviewDialogOpen(false)}
      />
    </section>
  );
}
